import { Mail, Phone, Pencil } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { MobileIcon, IdCardIcon } from "@radix-ui/react-icons";
import MemberInvoice from "./invoice_and_linked_members/member-invoices";
import { MemberTableDatatypes } from "@/types/member";
import { displayAddress, displayDate, displayValue } from "@/lib/helper";

import { useGetCountriesQuery } from "@/services/commonApi";

import { CountryTypes } from "@/types";
import { useMemo, useState } from "react";
import { useGetMembershipListQuery } from "@/services/membershipsApi";
import MemberForm from "../modal/form";
import { useGetAllMemberQuery } from "@/services/memberApi";
import MembershipPlanAndFacilities from "./membership_facilities";
import { useSearchParams } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type BusinessDetailProps = {
  memberInfo: MemberTableDatatypes | undefined;
};

export default function BusinessDetailTabs({
  memberInfo,
}: BusinessDetailProps) {
  const [searchParam, setSearchParams] = useSearchParams();

  const tabParam = searchParam.get("tab") || "member-data";

  const {
    hide_membership_facilites,
    hide_fitness_progress,
    hide_coaches,
    hide_invoices,
    hide_member_details,
  } = JSON.parse(localStorage.getItem("accessLevels") as string);

  const { data: countries } = useGetCountriesQuery();

  const membershipPlans = useMemo(() => {
    return Array.isArray(memberInfo?.membership_plans)
      ? memberInfo?.membership_plans.filter(
        (plan) => plan.membership_plan_name !== null || undefined
      )
      : [];
  }, [memberInfo]);

  const coaches = useMemo(() => {
    return Array.isArray(memberInfo?.coaches)
      ? memberInfo?.coaches.filter((coach) => coach.id !== 0)
      : [];
  }, [memberInfo]);

  const [action, setAction] = useState<"add" | "edit" | "member-created">(
    "add"
  );
  const [openMemberForm, setOpenMemberForm] = useState<boolean>(false);
  const [editMember, setEditMember] = useState<MemberTableDatatypes | null>(
    memberInfo as MemberTableDatatypes
  );

  function handleOpenForm() {
    setAction("edit");
    setEditMember(memberInfo as MemberTableDatatypes);
    setOpenMemberForm(true);
  }

  const tabs = [
    {
      value: "member-data",
      label: "Member Data",
      access: hide_member_details !== "no_access",
    },
    {
      value: "invoices",
      label: `Invoices & Linked Members`,
      access: hide_invoices !== "no_access",
    },
    // {
    //   value: "coaching",
    //   label: "Coaching",
    //   access: hide_coaches !== "no_access",
    // },
    // {
    //   value: "fitness",
    //   label: "Fitness Data & Progress",
    //   access: hide_fitness_progress !== "no_access",
    // },
    {
      value: "membership&facilities",
      label: "Membership Plan & Facilities",
      access: hide_membership_facilites !== "no_access",
    },
  ];
  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value }); // Update URL on tab change
  };
  const coachesList = coaches
    ?.map((item) =>
      item.name
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    )
    .join(", ");
  console.log({ coachesList });
  return (
    // <Card className=" w-[100%] sxl:w-[70%]  p-4">
    <>
      <Tabs
        value={tabParam}
        onValueChange={handleTabChange}
        // defaultValue="member-data"
        className="w-full"
      >
        <Card className=" w-[100%] sxl:w-[100%] p-4 border-b-0 rounded-none rounded-tl-md rounded-tr-md">
          <ScrollArea className="w-full">
            <ScrollBar
              orientation="horizontal"
              className="h-2 cursor-grabbing"
            />
            <TabsList className="border-b rounded-none w-full justify-start h-auto p-0 bg-transparent">
              {tabs
                .filter((tab) => tab.access)
                .map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="border-b-2 border-transparent data-[state=active]:border-primary rounded-none h-auto pb-2 px-4 text-muted-foreground"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
            </TabsList>
          </ScrollArea>
        </Card>

        <TabsContent value="member-data">
          <Card className=" w-[100%] sxl:w-[100%]  p-4 border-t-0  rounded-none rounded-bl-md rounded-br-md">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-semibold">Member Data</h2>
              <button
                onClick={handleOpenForm}
                className="p-2 hover:bg-muted rounded-full border"
              >
                <Pencil className="w-5 h-5" />
              </button>
            </div>

            <Card className="p-0">
              <CardContent className="py-5 space-y-3">
                <div className="space-y-2">
                  <h3 className="font-semibold">Basic Information</h3>
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground">
                      Member Id: {memberInfo?.own_member_id}
                    </div>
                    <div className="flex items-start justify-between">
                      <div className="flex flex-col gap-1">
                        <div className="font-medium capitalize">
                          <span>
                            {memberInfo?.first_name +
                              " " +
                              memberInfo?.last_name +
                              " "}
                          </span>
                          <span className="text-xs text-gray-500 font-normal">
                            {!memberInfo?.is_business &&
                              memberInfo?.business_id == undefined ? "" : memberInfo?.business_name}
                          </span>
                        </div>
                        <div className="text-sm ">
                          <span className="text-bold text-black">Coach: </span>
                          {coaches.length > 0 ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger
                                  asChild
                                  className="text-gray-500 font-normal"
                                >
                                  {coachesList.length > 20 ? (
                                    <span>
                                      {coachesList.substring(0, 40) + "..."}
                                    </span>
                                  ) : (
                                    <span>{coachesList}</span>
                                  )}
                                </TooltipTrigger>
                                <TooltipContent
                                  className="w-[40%] font-normal "
                                  side="right"
                                >
                                  {coachesList}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-sm text-gray-500">N/A</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-col">
                        <div className="w-full justify-end flex">
                          <Badge
                            variant="outline"
                            className={`h-5 ${memberInfo?.client_status == "active"
                              ? "bg-lightprimary border border-lightprimary text-lightprimaryborder"
                              : memberInfo?.client_status == "inactive"
                                ? "bg-blue-200 border border-blue-200 text-blue-700"
                                : "bg-yellow-200 border border-yellow-200 text-yellow-700"
                              } capitalize`}
                          >
                            {memberInfo?.client_status}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-6 text-sm">
                          <div className="text-muted-foreground capitalize text-nowrap">
                            Gender: {memberInfo?.gender}
                          </div>
                          <div className="text-muted-foreground text-nowrap">
                            Date of Birth: {displayDate(memberInfo?.dob)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <Separator className="!mb-3 mt-1" />
                  </div>

                  <div className="flex flex-col xlg:flex-row gap-4">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{memberInfo?.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">
                        {displayValue(memberInfo?.mobile_number)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MobileIcon className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">
                        {displayValue(memberInfo?.phone)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <IdCardIcon className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">
                        {displayValue(memberInfo?.nic)}
                      </span>
                    </div>
                  </div>
                </div>
                <Separator />
                <div className="space-y-2">
                  <h3 className="font-semibold">Address</h3>
                  <div className="space-y-2">
                    <div>
                      <div className="text-sm font-medium">Street Address:</div>
                      <div className="text-sm text-muted-foreground">
                        {displayAddress(
                          memberInfo?.address_1,
                          memberInfo?.address_2
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-8 text-sm">
                      <div>
                        <span className="font-medium">Zip Code:</span>{" "}
                        <span className="text-muted-foreground">
                          {displayValue(memberInfo?.zipcode)}
                        </span>
                      </div>
                      <Separator orientation="vertical" />
                      <div>
                        <span className="font-medium">Country:</span>{" "}
                        <span className="text-muted-foreground capitalize">
                          {
                            countries?.find(
                              (country: CountryTypes) =>
                                country.id == memberInfo?.country_id
                            )?.country
                          }
                        </span>
                      </div>
                      <Separator orientation="vertical" />

                      <div>
                        <span className="font-medium">City:</span>{" "}
                        <span className="text-muted-foreground">
                          {displayValue(memberInfo?.city)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <Separator />
                <div className="space-y-2">
                  <h3 className="font-semibold">Membership</h3>
                  <div className="flex flex-wrap gap-3">

                    {membershipPlans.length > 0 ? membershipPlans.map((plan) => (
                      <Badge className="w-fit bg-green-300">
                        <span className="font-medium text-gray-700 normal-case">
                          {plan.membership_plan_name}
                        </span>
                      </Badge>
                    )) : <>
                      <p className="pl-4">No memberships assigned...</p>
                    </>}
                  </div>
                  <Separator />
                </div>

                <div className="space-y-2">
                  <h3 className="font-semibold">Notes</h3>
                  <div className="text-sm text-muted-foreground">
                    {memberInfo?.notes ? memberInfo?.notes : "N/A"}
                  </div>
                </div>
              </CardContent>
            </Card>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="w-full">
          <Card className=" w-[100%] sxl:w-[100%]  p-4 border-t-0  rounded-none rounded-bl-md rounded-br-md">
            <MemberInvoice memberInfo={memberInfo} />
          </Card>
        </TabsContent>

        <TabsContent value="membership&facilities">
          <MembershipPlanAndFacilities />
        </TabsContent>
      </Tabs>

      <MemberForm
        open={openMemberForm}
        setOpen={setOpenMemberForm}
        memberData={editMember}
        setMemberData={setEditMember}
        action={action}
        setAction={setAction}
        breadcrumb="Member Details"
      />
      {/* </Card> */}
    </>
  );
}
